import * as React from "react";
import { toast } from "sonner";
import { Loader } from "lucide-react";
import { BsArrowReturnLeft } from "react-icons/bs";
import { IoChatbubblesOutline } from "react-icons/io5";
import { FaRegCopy, FaRegFilePdf } from "react-icons/fa";
import { FiEdit2 } from "react-icons/fi";
import { RiCloseLargeFill } from "react-icons/ri";

import { ReviewTileItem, Impact } from "@/types/tile.types";
import { DismissAllTilesRequest } from "@/types/review.types";
import { useAddAllAsComments } from "@/hooks/useAddAllAsComments";
import { useTileDocumentCreation } from "@/hooks/useTileDocumentCreation";
import { useDismissAllTiles } from "@/hooks/useDismissAllTiles";
import { useBulkWriteIt } from "@/hooks/useBulkWriteIt";
import { useBulkInsert } from "@/hooks/useBulkInsert";
import { useBulkCopy } from "@/hooks/useBulkCopy";
import { useCreateDocumentFromTiles } from "@/hooks/useCreateDocumentFromTiles";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { cn } from "@/lib/utils";
import { BulkWriteItProgressProvider, useBulkWriteItProgress } from "./BulkWriteItProgressContext";
import { BulkWriteItProgressDialog } from "./BulkWriteItProgressDialog";
import { BulkActionsProgressDialog } from "./BulkActionsProgressDialog";

interface TilesBulkActionsProps {
  filteredItems: ReviewTileItem[];
  documentId: string;
  selectedQueryTerms: string[];
  onOperationStateChange?: (isProcessing: boolean) => void;
  impactFilter: Impact;
}

export const TilesBulkActions: React.FC<TilesBulkActionsProps> = ({
  filteredItems,
  documentId,
  selectedQueryTerms,
  onOperationStateChange,
  impactFilter,
}) => {
  return (
    <BulkWriteItProgressProvider>
      <TilesBulkActionsContent
        filteredItems={filteredItems}
        documentId={documentId}
        selectedQueryTerms={selectedQueryTerms}
        onOperationStateChange={onOperationStateChange}
        impactFilter={impactFilter}
      />
    </BulkWriteItProgressProvider>
  );
};

const TilesBulkActionsContent: React.FC<TilesBulkActionsProps> = ({
  filteredItems,
  documentId,
  selectedQueryTerms,
  onOperationStateChange,
  impactFilter,
}) => {
  const dismissAllTilesMutation = useDismissAllTiles();
  const { mutate: mutateBulkWriteIt, isPending: isPendingBulkWriteIt } = useBulkWriteIt();
  const { createDocumentFromTiles, isCreatingDocument: isCreatingDocumentFromTiles } = useCreateDocumentFromTiles();

  const { isOpen, progress, statusMessage, failedTiles, hideProgress, showProgress } =
    useBulkWriteItProgress();

  const [isAddAsCommentsDialogOpen, setIsAddAsCommentsDialogOpen] = React.useState(false);
  const [isBulkInsertDialogOpen, setIsBulkInsertDialogOpen] = React.useState(false);

  const documentCreationHook = useTileDocumentCreation({
    onSuccess: (document) => toast.success(`Document "${document.title}" created successfully`),
    onError: (error) => toast.error(error),
  });

  const addAsCommentsHook = useAddAllAsComments({
    onComplete: (result) => {
      if (result.failed > 0) {
        toast.warning(`Completed: ${result.success} successful, ${result.failed} failed`);
      } else {
        toast.success(`Successfully completed ${result.success} actions`);
      }
      setTimeout(() => setIsAddAsCommentsDialogOpen(false), 1000);
    },
    onError: (error) => {
      toast.error(error);
      setIsAddAsCommentsDialogOpen(false);
    },
  });

  const bulkInsertHook = useBulkInsert({
    onComplete: (result) => {
      if (result.failed > 0) {
        toast.warning(`Completed: ${result.success} successful, ${result.failed} failed`);
      } else {
        toast.success(`Successfully completed ${result.success} actions`);
      }
      setTimeout(() => setIsBulkInsertDialogOpen(false), 1000);
    },
    onError: (error) => {
      toast.error(error);
      setIsBulkInsertDialogOpen(false);
    },
  });

  const bulkCopyHook = useBulkCopy({
    onComplete: (result) => {
      if (result.failed > 0) {
        toast.warning(`Completed: ${result.success} successful, ${result.failed} failed`);
      } else {
        toast.success(`Successfully copied ${result.success} tiles`);
      }
    },
    onError: (error) => {
      toast.error(error);
    },
  });

  const isAnyOperationInProgress =
    documentCreationHook.isCreating ||
    addAsCommentsHook.isProcessing ||
    dismissAllTilesMutation.isPending ||
    isPendingBulkWriteIt ||
    bulkInsertHook.isProcessing ||
    bulkCopyHook.isProcessing ||
    isCreatingDocumentFromTiles;

  React.useEffect(() => {
    onOperationStateChange?.(isAnyOperationInProgress);
  }, [isAnyOperationInProgress, onOperationStateChange]);

  const handleBulkAction = async (action: string) => {
    if (filteredItems.length === 0) {
      toast.warning("No tiles to process");
      return;
    }

    try {
      switch (action) {
        case "writeItAll":
          setIsBulkInsertDialogOpen(true);
          await bulkInsertHook.insertAllTiles(filteredItems);
          break;
        case "addAsComments":
          setIsAddAsCommentsDialogOpen(true);
          await addAsCommentsHook.addAllAsComments(filteredItems);
          break;
        case "createDocumentFromTiles":
          await createDocumentFromTiles(filteredItems, "MS Word Document Review Items");
          break;
        case "copyAllTiles":
          await bulkCopyHook.copyAllTiles(filteredItems);
          break;
        case "bulkWriteIt":
          showProgress();
          mutateBulkWriteIt({
            items: filteredItems,
            documentId: documentId,
          });
          break;
        default:
          console.warn("Unknown action:", action);
      }
    } catch (error) {
      console.error(`Error in bulk action ${action}:`, error);
    }
  };

  const handleCloseAll = async () => {
    if (filteredItems.length === 0) {
      toast.warning("No tiles to dismiss");
      return;
    }

    const request: DismissAllTilesRequest = {
      document_id: [documentId],
      query_term: selectedQueryTerms,
    };

    dismissAllTilesMutation.mutate(request, {
      onSuccess: () => {
        toast.success("All tiles dismissed successfully");
      },
      onError: (error) => {
        toast.error("Error dismissing all tiles:", { description: error.message });
      },
    });
  };

  const impactText = impactFilter === "ALL" ? "all" : impactFilter.toLowerCase();

  const bulkActions = React.useMemo(
    () => [
      {
        action: "writeItAll",
        onClick: () => handleBulkAction("writeItAll"),
        disabled: isAnyOperationInProgress || filteredItems.length === 0,
        content: `Insert ${impactText} tiles`,
        icon: <BsArrowReturnLeft className="size-3.5 text-neutral-400" />,
        loading: bulkInsertHook.isProcessing,
        className: "",
      },
      {
        action: "addAsComments",
        onClick: () => handleBulkAction("addAsComments"),
        disabled: isAnyOperationInProgress || filteredItems.length === 0,
        content: `Add comments for ${impactText} tiles`,
        icon: <IoChatbubblesOutline className="size-3.5 text-neutral-400" />,
        loading: addAsCommentsHook.isProcessing,
        className: "",
      },
      {
        action: "createDocumentFromTiles",
        onClick: () => handleBulkAction("createDocumentFromTiles"),
        disabled: isAnyOperationInProgress || filteredItems.length === 0,
        content: "Create a new Word document from all tiles",
        icon: <FaRegFilePdf className="size-3.5 text-neutral-400" />,
        loading: isCreatingDocumentFromTiles,

        className: "",
      },
      {
        action: "copyAllTiles",
        onClick: () => handleBulkAction("copyAllTiles"),
        disabled: isAnyOperationInProgress || filteredItems.length === 0,
        content: `Copy ${impactText} tiles`,
        icon: <FaRegCopy className="size-3.5 text-neutral-400" />,
        loading: bulkCopyHook.isProcessing,

        className: "",
      },
      {
        action: "bulkWriteIt",
        onClick: () => handleBulkAction("bulkWriteIt"),
        disabled: isAnyOperationInProgress || filteredItems.length === 0,
        content: "Bulk Write It",
        icon: <FiEdit2 className="size-3.5 text-neutral-400" />,
        loading: isPendingBulkWriteIt,
        className: "",
      },
      {
        action: "closeAll",
        onClick: handleCloseAll,
        disabled: isAnyOperationInProgress || filteredItems.length === 0,
        content: "Close all tiles",
        icon: <RiCloseLargeFill className="size-3.5 text-neutral-400" />,
        loading: dismissAllTilesMutation.isPending,
        className: "text-danger-200",
      },
    ],
    [
      filteredItems.length,
      isAnyOperationInProgress,
      addAsCommentsHook.isProcessing,
      bulkInsertHook.isProcessing,
      bulkCopyHook.isProcessing,
      documentCreationHook.isCreating,
      isCreatingDocumentFromTiles,
      dismissAllTilesMutation.isPending,
      isPendingBulkWriteIt,
      showProgress,
      impactText,
    ]
  );

  const addAsCommentsProgressPercentage =
    addAsCommentsHook.progress.total > 0
      ? (addAsCommentsHook.progress.completed / addAsCommentsHook.progress.total) * 100
      : 0;

  const addAsCommentsMessage =
    addAsCommentsHook.progress.completed < addAsCommentsHook.progress.total ? (
      <>
        Processed <b>{addAsCommentsHook.progress.completed}</b> of <b>{addAsCommentsHook.progress.total}</b> tiles...
      </>
    ) : (
      `Completed processing ${addAsCommentsHook.progress.total} tiles.`
    );

  const bulkInsertProgressPercentage =
    bulkInsertHook.progress.total > 0
      ? (bulkInsertHook.progress.completed / bulkInsertHook.progress.total) * 100
      : 0;

  const bulkInsertMessage =
    bulkInsertHook.progress.completed < bulkInsertHook.progress.total ? (
      <>
        Processed <b>{bulkInsertHook.progress.completed}</b> of <b>{bulkInsertHook.progress.total}</b> tiles...
      </>
    ) : (
      `Completed processing ${bulkInsertHook.progress.total} tiles.`
    );

  return (
    <>
      <div className="flex items-center gap-3 flex-wrap">
        {bulkActions.map((action) => (
          <TooltipProvider key={action.action}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div
                  role="button"
                  className={cn("size-auto p-0 has-[>svg]:p-0", action.className)}
                  onClick={action.onClick}
                >
                  {action.loading ? <Loader className="animate-spin" /> : action.icon}
                </div>
              </TooltipTrigger>
              <TooltipContent>{action.content}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ))}
      </div>
      <BulkWriteItProgressDialog
        isOpen={isOpen}
        onClose={hideProgress}
        progress={progress}
        statusMessage={statusMessage}
        failedTiles={failedTiles}
      />
      <BulkActionsProgressDialog
        isOpen={isAddAsCommentsDialogOpen}
        onClose={() => setIsAddAsCommentsDialogOpen(false)}
        progress={addAsCommentsProgressPercentage}
        message={addAsCommentsMessage}
        title="Adding Comments"
      />
      <BulkActionsProgressDialog
        isOpen={isBulkInsertDialogOpen}
        onClose={() => setIsBulkInsertDialogOpen(false)}
        progress={bulkInsertProgressPercentage}
        message={bulkInsertMessage}
        title="Inserting Tiles"
      />
    </>
  );
};

export default TilesBulkActions;
